import React from 'react';
import Box from '@mui/material/Box';
import Button from '@mui/material/Button';
import DialogActions from '@mui/material/DialogActions';
import DialogContent from '@mui/material/DialogContent';
import DialogTitle from '@mui/material/DialogTitle';
import FormControl from '@mui/material/FormControl';
import IconButton from '@mui/material/IconButton';
import InputAdornment from '@mui/material/InputAdornment';
import InputLabel from '@mui/material/InputLabel';
import MenuItem from '@mui/material/MenuItem';
import Select from '@mui/material/Select';
import TextField from '@mui/material/TextField';
import Typography from '@mui/material/Typography';
import DeleteOutlineIcon from '@mui/icons-material/DeleteOutline';
import PersonAddAltIcon from '@mui/icons-material/PersonAddAlt';

import { StackedDialog, useSelectMenuProps } from '../utilities/ModalStackContext';
import {
  creditsAreValid,
  normalizeCredits,
  splitEvenly,
  totalShare,
  useSalesTeam,
} from '../utilities/salesCredits';

/**
 * Editor for a sales credit list — see `src/utilities/salesCredits.js` for the shape.
 *
 * Two exports because credit is chosen at two different moments. On a service order it
 * is changed after the fact, so it gets its own dialog. On an NTO reduction it is part
 * of recording the change, so `SalesCreditEditor` drops inline into UpdateNTODialog and
 * saves in the same write as the amount.
 */

const rowKey = (row, i) => `${row.rep_id || row.rep_name || 'row'}-${i}`;

export function SalesCreditEditor({ value, onChange, disabled = false }) {
  const salesTeam = useSalesTeam();
  const selectMenuProps = useSelectMenuProps();
  const rows = React.useMemo(() => (Array.isArray(value) ? value : []), [value]);
  const total = totalShare(rows);
  // Rows that are still being edited can be blank, so count them rather than relying on
  // normalizeCredits — an empty picker should not read as "no credit".
  const isEmpty = rows.length === 0;

  // A rep who has left the sales team can still be on an old credit. Keep them
  // selectable so opening the editor cannot silently drop their name.
  const options = React.useMemo(() => {
    const extras = rows
      .filter(row => (row.rep_id || row.rep_name)
        && !salesTeam.some(t => t.id === row.rep_id || t.name === row.rep_name))
      .map(row => ({ id: row.rep_id || row.rep_name, name: row.rep_name || row.rep_id }));
    return [...salesTeam, ...extras];
  }, [salesTeam, rows]);

  const setRow = (i, patch) => {
    onChange(rows.map((row, index) => (index === i ? { ...row, ...patch } : row)));
  };

  const addRow = () => {
    // The first rep added takes all of it; after that, even shares are the sane guess.
    const next = [...rows, { rep_id: null, rep_name: '', share: rows.length ? 0 : 100 }];
    onChange(rows.length ? splitEvenly(next) : next);
  };

  const removeRow = (i) => {
    const next = rows.filter((row, index) => index !== i);
    // Removing one of two reps should leave the other with the whole thing, not 50%.
    onChange(next.length === 1 ? [{ ...next[0], share: 100 }] : next);
  };

  return (
    <Box>
      {isEmpty && (
        <Typography variant="body2" color="text.secondary" sx={{ mb: 1.5 }}>
          No rep is credited. Nobody is paid for this.
        </Typography>
      )}

      {rows.map((row, i) => (
        <Box key={rowKey(row, i)} sx={{ display: 'flex', gap: 1.5, alignItems: 'center', mb: 1.5 }}>
          <FormControl size="small" sx={{ flex: 1, minWidth: 180 }}>
            <InputLabel>Rep</InputLabel>
            <Select
              label="Rep"
              disabled={disabled}
              MenuProps={selectMenuProps}
              value={row.rep_id || row.rep_name || ''}
              onChange={(e) => {
                const picked = options.find(t => t.id === e.target.value);
                setRow(i, {
                  rep_id: picked && salesTeam.some(t => t.id === picked.id) ? picked.id : null,
                  rep_name: picked ? picked.name : '',
                });
              }}
            >
              {options.map(t => (
                <MenuItem key={t.id} value={t.id}>{t.name}</MenuItem>
              ))}
            </Select>
          </FormControl>
          <TextField
            size="small"
            type="number"
            label="Share"
            disabled={disabled || rows.length === 1}
            value={row.share === 0 ? '' : row.share}
            onChange={(e) => setRow(i, { share: e.target.value === '' ? 0 : Number(e.target.value) })}
            inputProps={{ min: 0, max: 100 }}
            InputProps={{ endAdornment: <InputAdornment position="end">%</InputAdornment> }}
            sx={{ width: 110 }}
          />
          <IconButton
            size="small"
            disabled={disabled}
            onClick={() => removeRow(i)}
            aria-label="Remove rep"
          >
            <DeleteOutlineIcon fontSize="small" />
          </IconButton>
        </Box>
      ))}

      <Box sx={{ display: 'flex', gap: 1, alignItems: 'center', flexWrap: 'wrap' }}>
        <Button
          size="small"
          startIcon={<PersonAddAltIcon />}
          onClick={addRow}
          disabled={disabled}
        >
          Add rep
        </Button>
        {rows.length > 1 && (
          <Button size="small" onClick={() => onChange(splitEvenly(rows))} disabled={disabled}>
            Split evenly
          </Button>
        )}
        {rows.length > 0 && (
          <Typography
            variant="caption"
            sx={{ ml: 'auto', fontWeight: 600 }}
            color={total === 100 ? 'success.dark' : 'error.main'}
          >
            {total}% of 100%
          </Typography>
        )}
      </Box>
    </Box>
  );
}

export default function SalesCreditDialog({
  open,
  onClose,
  onSave,
  value,
  title = 'Sales Credit',
  subtitle = '',
  helperText = '',
  saving = false,
}) {
  const [credits, setCredits] = React.useState([]);

  React.useEffect(() => {
    if (open) setCredits(normalizeCredits(value));
  }, [open, value]);

  // A row with no rep picked yet is incomplete, not a saveable "no credit".
  const incomplete = credits.some(row => !row.rep_id && !row.rep_name);
  const canSave = !incomplete && creditsAreValid(credits);

  return (
    <StackedDialog open={open} onClose={onClose} fullWidth maxWidth="sm">
      <DialogTitle>
        <Typography variant="h6" sx={{ fontWeight: 600 }}>{title}</Typography>
        {subtitle && (
          <Typography variant="body2" color="textSecondary">{subtitle}</Typography>
        )}
      </DialogTitle>
      <DialogContent dividers>
        {helperText && (
          <Typography variant="body2" color="text.secondary" sx={{ mb: 2 }}>
            {helperText}
          </Typography>
        )}
        <SalesCreditEditor value={credits} onChange={setCredits} disabled={saving} />
      </DialogContent>
      <DialogActions>
        <Button onClick={() => onSave([])} disabled={saving} color="inherit">
          No credit
        </Button>
        <Box sx={{ flex: 1 }} />
        <Button onClick={onClose} disabled={saving}>Cancel</Button>
        <Button
          variant="contained"
          onClick={() => onSave(normalizeCredits(credits))}
          disabled={saving || !canSave}
        >
          {saving ? 'Saving…' : 'Save'}
        </Button>
      </DialogActions>
    </StackedDialog>
  );
}
